if (!customElements.get("geolocation-recommendation")) {
  customElements.define(
    "geolocation-recommendation",
    class GeolocationRecommendation extends ModalDialog {
      constructor() {
        super();

        this.activeCountry = this.dataset.activeCountry;
        this.activeCountryName = this.dataset.activeCountryName || this.activeCountry;
        this.activeCurrency = this.dataset.activeCurrency || "";
        this.cartItemCount = Number(this.dataset.cartItemCount || 0);
        this.previouslyFocusedElement = null;
        this.currentPrompt = null;
        this.suppressedForPage = false;
        this.form = this.querySelector("form");
        this.titleElement = this.querySelector("#GeolocationRecommendationTitle");
        this.descriptionElement = this.querySelector("#GeolocationRecommendationDescription");
        this.switchButton = this.querySelector("[data-geolocation-switch]");
        this.switchFlag = this.querySelector("[data-geolocation-switch-flag]");
        this.switchLabel = this.querySelector("[data-geolocation-switch-label]");
        this.stayButton = this.querySelector("[data-stay-current]");
        this.config = this.readConfig();

        this.switchButton?.addEventListener("click", () => this.switchToSuggestedCountry());
        this.stayButton?.addEventListener("click", () => this.dismiss("stay"));
      }

      connectedCallback() {
        super.connectedCallback();
        window.geolocationRecommendationModal = this;

        if (window.Shopify?.designMode) {
          return;
        }

        this.detect();
      }

      async detect() {
        if (
          !this.activeCountry ||
          !this.config.countries.some((country) => country.code === this.activeCountry) ||
          this.cartItemCount > 0 ||
          this.suppressedForPage ||
          window.MarketSwitch?.shouldSuppressAdvice?.(this.activeCountry)
        ) {
          return;
        }

        const suggestion = await this.fetchSuggestion();
        const suggestedCountry = suggestion?.handle;
        const country = this.config.countries.find(
          (candidate) => candidate.code === suggestedCountry,
        );

        if (!country || suggestedCountry === this.activeCountry) {
          return;
        }

        this.preparePrompt({
          country,
          detectedName: suggestion.name || country.name,
        });
        this.show();
      }

      async fetchSuggestion() {
        const root = window.Shopify?.routes?.root || "/";
        const endpoint = `${root}browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${encodeURIComponent(this.activeCountry)}`;

        try {
          const response = await fetch(endpoint);

          if (!response.ok) {
            return null;
          }

          const data = await response.json();
          return data?.detected_values?.country || null;
        } catch (_error) {
          return null;
        }
      }

      preparePrompt(prompt) {
        this.currentPrompt = prompt;
        this.hidden = false;
        this.renderPrompt(prompt);
      }

      renderPrompt(prompt) {
        const replacements = {
          active_country: this.activeCountryName,
          active_currency: this.activeCurrency,
          detected_country: prompt.detectedName,
          detected_currency: prompt.country.currencyCode,
          detected_flag: this.countryFlag(prompt.country.code),
        };

        this.titleElement.textContent = this.interpolate(this.config.strings.heading, replacements);
        this.descriptionElement.textContent = this.interpolate(
          this.config.strings.body,
          replacements,
        );
        this.switchButton.setAttribute(
          "aria-label",
          this.interpolate(this.config.strings.shopAction, replacements),
        );
        this.switchFlag.textContent = this.countryFlag(prompt.country.code);
        this.switchLabel.textContent = this.interpolate(
          this.config.strings.shopAction,
          replacements,
        );
        this.stayButton.textContent = this.interpolate(
          this.config.strings.stayAction,
          replacements,
        );
      }

      show(opener) {
        this.previouslyFocusedElement = opener || document.activeElement;
        super.show(opener);

        if (this.switchButton) {
          trapFocus(this, this.switchButton);
        }

        document.dispatchEvent(new CustomEvent("geolocation-recommendation:open"));
      }

      hide() {
        this.dismiss("close");
      }

      dismiss(action) {
        const wasOpen = this.hasAttribute("open");

        this.suppressForVisit();
        ModalDialog.prototype.hide.call(this);
        this.hidden = true;

        if (this.previouslyFocusedElement?.isConnected) {
          removeTrapFocus(this.previouslyFocusedElement);
        }

        if (wasOpen) {
          document.dispatchEvent(
            new CustomEvent("geolocation-recommendation:close", {
              detail: { action },
            }),
          );
        }
      }

      suppressForVisit() {
        this.suppressedForPage = true;
        window.MarketSwitch?.suppressAdvice?.(this.activeCountry);
      }

      switchToSuggestedCountry() {
        const country = this.currentPrompt?.country;

        if (!country || !this.form) {
          this.dismiss("close");
          return;
        }

        this.dismiss("switch");
        window.MarketSwitch?.requestCountrySwitch?.(
          this.form,
          country.code,
          "geolocation",
        );
      }

      countryFlag(countryCode) {
        return {
          US: "🇺🇸",
          CA: "🇨🇦",
        }[countryCode] || "🌐";
      }

      interpolate(value, replacements) {
        const interpolated = String(value || "").replace(/\[([a-z_]+)\]/g, (match, key) => {
          return replacements[key] || match;
        });

        const parser = document.createElement("textarea");
        parser.innerHTML = interpolated;
        return parser.value;
      }

      readConfig() {
        const configElement = this.querySelector("[data-geolocation-config]");
        const fallback = {
          countries: [],
          strings: {},
        };

        if (!configElement?.textContent) {
          return fallback;
        }

        try {
          return {
            ...fallback,
            ...JSON.parse(configElement.textContent),
          };
        } catch (_error) {
          return fallback;
        }
      }

      showForTest() {
        const country = this.config.countries.find(
          (candidate) => candidate.code !== this.activeCountry,
        );

        if (!country) {
          return;
        }

        this.preparePrompt({ country, detectedName: country.name });
        this.show();
      }
    },
  );
}
